import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
    updateBookHoldStatus,
    getProductById,
    getAllOrdersList,
    cancelOrder,
    getOrderDetails,
    getOrderStatuses,
    updateOrder,
    searchProducts
} from "../../../api/productApi";
import {
    normalizeOrderFromList,
    normalizeOrderDetails
} from "../services/orderTransformer";
import useDamageForm from "../../../hooks/useDamageForm";

export default function useOrderFulfillment() {
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");

    /* ---------- ORDERS LIST STATE (LEFT PANEL) ---------- */
    const [orders, setOrders] = useState([]);
    const [orderQuery, setOrderQuery] = useState("");
    const [debouncedOrderQuery, setDebouncedOrderQuery] = useState("");
    const [allOrdersQuery, setAllOrdersQuery] = useState("");
    const [debouncedAllOrdersQuery, setDebouncedAllOrdersQuery] = useState("");
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [loadingMoreOrders, setLoadingMoreOrders] = useState(false);
    const [mode, setMode] = useState("INITIAL"); // "INITIAL" | "SEARCH_RESULT" | "ALL_ORDERS"

    const [statusCounts, setStatusCounts] = useState({
        all: 0,
        cancelled: 0,
        pending: 0,
        processing: 0,
        completed: 0,
    });
    const [countsLoaded, setCountsLoaded] = useState(false);

    const listContainerRef = useRef(null);
    const fetchIdRef = useRef(0);
    const pageRef = useRef(1);
    const totalPagesRef = useRef(1);
    const isLoadingRef = useRef(false);

    /* ---------- DETAIL VIEW STATE (RIGHT PANEL) ---------- */
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [detailsError, setDetailsError] = useState("");
    const [cartLoading, setCartLoading] = useState(false);
    const [cartError, setCartError] = useState("");
    const [cancelingOrder, setCancelingOrder] = useState(false);
    const [memberLoading, setMemberLoading] = useState(false);

    const orderDetailsCacheRef = useRef({});
    const memberCacheRef = useRef({});

    const [memberInfo, setMemberInfo] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [originalItems, setOriginalItems] = useState([]);
    const [packedItems, setPackedItems] = useState([]);

    useEffect(() => {
        setPackedItems([]);
    }, [selectedOrder?.order_id]);


    useEffect(() => {
        console.log("[DEBUG] Mapped memberInfo:", memberInfo);
        console.log("[DEBUG] Rendered billing address:", memberInfo?.billing_address);
    }, [memberInfo]);

    useEffect(() => {
        console.log("mapped selectedOrder", selectedOrder);
    }, [selectedOrder]);

    /* ---------- ACTION DIALOG STATES ---------- */
    const [toast, setToast] = useState("");
    const [showDamageModal, setShowDamageModal] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [damageRemoveFromOrder, setDamageRemoveFromOrder] = useState(true);

    const [actionSubmitting, setActionSubmitting] = useState(false);

    /* ---------- LOCAL CARD ACTIONS STATE ---------- */
    const [damageTargetItem, setDamageTargetItem] = useState(null);

    const damageBookObj = useMemo(() => {
        if (!damageTargetItem) return null;
        return {
            ...damageTargetItem,
            id: damageTargetItem.product_id
        };
    }, [damageTargetItem]);

    const damageFormObj = useDamageForm({
        book: damageBookObj,
        token,
        initialMemberId: memberInfo?.id || memberInfo?.client_id || "",
        initialOrderId: selectedOrder?.order_id || "",
        onSaveSuccess: async (payload) => {
            const orderId = selectedOrder?.order_id;
            if (!orderId) return;

            showToast("Damage charges logged successfully!", "success");
            setShowDamageModal(false);

            // Refresh order details to update member information & damage charges
            try {
                // Delete from cache so we get the fresh data
                delete orderDetailsCacheRef.current[orderId];

                const detailsRes = await getOrderDetails({ token, orderId });
                let orderData = detailsRes;
                if (detailsRes?.data) {
                    orderData = detailsRes.data.data || detailsRes.data;
                }
                if (orderData) {
                    const finalOrderObj = normalizeOrderDetails(orderData);
                    if (finalOrderObj) {
                        finalOrderObj.member_info = orderData.member_info;

                        // We filter out the item locally if it was marked to be removed
                        if (damageRemoveFromOrder) {
                            setSelectedOrder(prev => {
                                const nextOrderObj = filterCartItem(finalOrderObj, damageTargetItem?.product_id);
                                orderDetailsCacheRef.current[orderId] = nextOrderObj;
                                return nextOrderObj;
                            });
                            setPackedItems(prev => prev.filter(x => x.product_id !== damageTargetItem?.product_id));
                        } else {
                            setSelectedOrder(finalOrderObj);
                            orderDetailsCacheRef.current[orderId] = finalOrderObj;
                        }

                        if (orderData.member_info) {
                            const rawMember = orderData.member_info;
                            const mappedMember = {
                                id: rawMember.client_id || rawMember.id || "",
                                name: rawMember.name || "",
                                phone: rawMember.phone || "",
                                client_id: rawMember.client_id || "",
                                email: rawMember.email || "",
                                subscription: rawMember.subscription || "",
                                deposit: Number(rawMember.deposit_amount || 0),
                                damageCharges: Number(rawMember.damage_charges || 0),
                                remaining_amount: Number(rawMember.remaining_amount || 0),
                                credits: Number(rawMember.credits || 0),
                                billing_address: rawMember.billing_address || "",
                                overdue_charges: rawMember.overdue_charges || 0,
                                overdue_charges_details: rawMember.overdue_charges_details || []
                            };
                            setMemberInfo(mappedMember);
                        }
                    }
                }
            } catch (refreshErr) {
                console.error("Failed to refresh order details after damage report", refreshErr);
            }

            setDamageTargetItem(null);
        }
    });

    const damageSubmitting = damageFormObj.saving;
    const damageForm = {
        amt: damageFormObj.amount,
        reason: damageFormObj.reason,
        severity: damageFormObj.severity,
        copyId: damageFormObj.copyId,
        date: damageFormObj.date,
        remark: damageFormObj.remark,
        sendEmail: damageFormObj.sendEmail,
        images: damageFormObj.imagesBase64
    };

    const setDamageForm = (updater) => {
        const next = typeof updater === "function" ? updater(damageForm) : updater;
        if (next.reason !== undefined) damageFormObj.setReason(next.reason);
        if (next.severity !== undefined) damageFormObj.setSeverity(next.severity);
        if (next.copyId !== undefined) damageFormObj.setCopyId(next.copyId);
        if (next.date !== undefined) damageFormObj.setDate(next.date);
        if (next.amt !== undefined) damageFormObj.setAmount(next.amt);
        if (next.remark !== undefined) damageFormObj.setRemark(next.remark);
        if (next.sendEmail !== undefined) damageFormObj.setSendEmail(next.sendEmail);
        if (next.images !== undefined) damageFormObj.setImagesBase64(next.images);
    };
    const [replacingItem, setReplacingItem] = useState(null);
    const [showScanReplaceModal, setShowScanReplaceModal] = useState(false);
    const [scannedReplaceId, setScannedReplaceId] = useState("");
    const [scanningReplace, setScanningReplace] = useState(false);
    const [scanReplaceError, setScanReplaceError] = useState("");
    const [activeLeftDropdown, setActiveLeftDropdown] = useState(null);
    const [activeStatusFilter, setActiveStatusFilter] = useState("all");

    /* ---------- MANUAL ADD BOOK STATE ---------- */
    const [copySearchQuery, setCopySearchQuery] = useState("");
    const [copySearchLoading, setCopySearchLoading] = useState(false);

    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [selectedBookFromSearch, setSelectedBookFromSearch] = useState(null);

    useEffect(() => {
        if (selectedBookFromSearch && (selectedBookFromSearch.name || selectedBookFromSearch.title) === copySearchQuery) {
            return;
        }

        const query = copySearchQuery.trim();
        if (query.length < 2) {
            setSearchResults([]);
            setShowDropdown(false);
            return;
        }

        const delayDebounce = setTimeout(async () => {
            setSearchLoading(true);
            setShowDropdown(true);
            try {
                let res = null;
                // If it looks like a Copy ID (e.g. pure numeric), try copy_id search first
                if (/^\d{6,}$/.test(query)) {
                    res = await searchProducts({
                        token,
                        copy_id: query,
                        page: 1,
                        perPage: 10
                    });
                }

                // If no results from copy_id or it's not a numeric ID, search by q
                if (!res || !res.data || res.data.length === 0) {
                    res = await searchProducts({
                        token,
                        q: query,
                        page: 1,
                        perPage: 100
                    });
                }
                setSearchResults(res?.data || []);
            } catch (err) {
                console.error("Search books error", err);
                setSearchResults([]);
            } finally {
                setSearchLoading(false);
            }
        }, 400);

        return () => clearTimeout(delayDebounce);
    }, [copySearchQuery, selectedBookFromSearch, token]);

    const showToast = useCallback((msg, type = "success") => {
        setToast(`${type === "success" ? "✅" : "❌"} ${msg}`);
        setTimeout(() => setToast(""), 3500);
    }, []);

    /* ---------- ORDER STATUSES FROM API ---------- */
    const [orderStatuses, setOrderStatuses] = useState([]);
    const [statusesLoading, setStatusesLoading] = useState(true);
    const [statusesError, setStatusesError] = useState(false);

    useEffect(() => {
        let active = true;
        async function loadStatuses() {
            try {
                setStatusesLoading(true);
                setStatusesError(false);
                const res = await getOrderStatuses({ token });
                if (res && res.success && res.data) {
                    const mapped = Object.entries(res.data).map(([key, val]) => ({
                        value: key,
                        label: val,
                    }));
                    if (active) {
                        setOrderStatuses(mapped);
                    }
                } else {
                    throw new Error("Failed response structure");
                }
            } catch (err) {
                console.error("Failed to load order statuses:", err);
                if (active) {
                    setStatusesError(true);
                    showToast("Failed to load order statuses", "error");
                }
            } finally {
                if (active) {
                    setStatusesLoading(false);
                }
            }
        }
        loadStatuses();
        return () => {
            active = false;
        };
    }, [token, showToast]);

    /* ---------- DEBOUNCE SEARCH ---------- */
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedOrderQuery(orderQuery.trim());
        }, 500);
        return () => clearTimeout(timer);
    }, [orderQuery]);

    /* ---------- DEBOUNCE ALL ORDERS SEARCH ---------- */
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedAllOrdersQuery(allOrdersQuery.trim());
        }, 500);
        return () => clearTimeout(timer);
    }, [allOrdersQuery]);

    /* ---------- STATUS COUNTS FETCHING ---------- */
    const fetchStatusCounts = useCallback(async () => {
        if (!token) return;
        try {
            const res = await getAllOrdersList({
                token,
                page: 1,
                perPage: 200,
                search: "",
                status: "all",
            });

            const all = res.data || [];
            const counts = res.status_counts || null;

            if (counts) {
                setStatusCounts({
                    all: Number(counts.all || counts.total || all.length),
                    cancelled: Number(counts.cancelled || counts.canceled || 0),
                    pending: Number(counts.pending || 0),
                    processing: Number(counts.processing || 0),
                    completed: Number(counts.completed || 0),
                });
            } else {
                const cancelled = all.filter(o => ["cancelled", "trash", "failed"].includes((o.status || "").toLowerCase())).length;
                const pending = all.filter(o => ["pending", "on-hold", "on_hold"].includes((o.status || "").toLowerCase())).length;
                const processing = all.filter(o => (o.status || "").toLowerCase() === "processing").length;
                const completed = all.filter(o => (o.status || "").toLowerCase() === "completed").length;
                const total = res.pagination?.total_count || all.length;
                setStatusCounts({ all: total, cancelled, pending, processing, completed });
            }
            setCountsLoaded(true);
        } catch (err) {
            console.error("Failed to fetch status counts:", err);
        }
    }, [token]);

    useEffect(() => {
        if (mode === "ALL_ORDERS") {
            fetchStatusCounts();
        }
    }, [fetchStatusCounts, mode]);

    /* ---------- ORDERS LIST FETCHING ---------- */
    const fetchOrdersList = useCallback(async (targetPage, isAppend = false) => {
        if (!token) return;
        const currentFetchId = ++fetchIdRef.current;
        isLoadingRef.current = true;

        if (targetPage === 1) {
            setLoadingOrders(true);
        } else {
            setLoadingMoreOrders(true);
        }

        try {
            const res = await getAllOrdersList({
                token,
                page: targetPage,
                perPage: 20,
                search: debouncedAllOrdersQuery,
                status: activeStatusFilter,
            });

            if (currentFetchId !== fetchIdRef.current) return;

            const rawOrders = res?.data || [];
            const loaded = rawOrders.map(normalizeOrderFromList);

            if (isAppend && targetPage > 1) {
                setOrders((prev) => {
                    const existingIds = new Set(prev.map((x) => x.order_id));
                    const filtered = loaded.filter((x) => !existingIds.has(x.order_id));
                    return [...prev, ...filtered];
                });
            } else {
                setOrders(loaded);
            }

            pageRef.current = res?.pagination?.current_page || targetPage;
            totalPagesRef.current = res?.pagination?.total_pages || 1;
        } catch (err) {
            console.error("Failed to fetch orders list:", err);
            if (currentFetchId === fetchIdRef.current && !isAppend) {
                setOrders([]);
            }
        } finally {
            if (currentFetchId === fetchIdRef.current) {
                isLoadingRef.current = false;
                setLoadingOrders(false);
                setLoadingMoreOrders(false);
            }
        }
    }, [token, debouncedAllOrdersQuery, activeStatusFilter]);

    useEffect(() => {
        if (mode !== "ALL_ORDERS") return;
        pageRef.current = 1;
        totalPagesRef.current = 1;
        isLoadingRef.current = true;
        setOrders([]);
        if (listContainerRef.current) {
            listContainerRef.current.scrollTop = 0;
        }
        fetchOrdersList(1, false);
    }, [fetchOrdersList, mode]);

    const loadMoreOrders = useCallback(async () => {
        if (isLoadingRef.current || loadingMoreOrders || loadingOrders) return;
        if (pageRef.current >= totalPagesRef.current) return;
        await fetchOrdersList(pageRef.current + 1, true);
    }, [loadingMoreOrders, loadingOrders, fetchOrdersList]);

    useEffect(() => {
        const el = listContainerRef.current;
        if (!el) return;
        const onScroll = () => {
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 100) {
                loadMoreOrders();
            }
        };
        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, [loadMoreOrders]);

    useEffect(() => {
        if (!activeLeftDropdown) return;
        const handler = () => setActiveLeftDropdown(null);
        document.addEventListener("click", handler);
        return () => document.removeEventListener("click", handler);
    }, [activeLeftDropdown]);

    const filterCartItem = useCallback((prev, productId) => {
        if (!prev) return prev;
        const item = prev.items.find(x => x.product_id === productId);
        const remainingItems = prev.items.filter(x => x.product_id !== productId);
        if (!item) return { ...prev, items: remainingItems };
        const price = item.price;
        const qty = item.quantity;
        const itemTotal = price * qty;
        return {
            ...prev,
            items: remainingItems,
            cart_item_count: Math.max(0, prev.cart_item_count - qty),
            cart_subtotal: Math.max(0, prev.cart_subtotal - itemTotal),
            cart_total: Math.max(0, prev.cart_total - itemTotal)
        };
    }, []);





    /* ---------- SELECT ORDER & PROGRESSIVE FETCH ---------- */
    const selectOrder = useCallback(async (order) => {
        console.time("Total click-to-render duration");

        const orderId = order.order_id;
        console.log("[DEBUG] selectOrder called with orderId:", orderId, "order:", order);
        if (!orderId) return;

        setDetailsError("");
        setCartError("");
        setLoadingDetails(true);
        setCartLoading(true);
        setMemberLoading(true);

        const cachedOrder = orderDetailsCacheRef.current[orderId];

        if (cachedOrder) {
            setSelectedOrder(cachedOrder);
            setOriginalItems(cachedOrder.items || []);
            if (cachedOrder.member_info) {
                const rawMember = cachedOrder.member_info;
                const mappedMember = {
                    id: rawMember.client_id || rawMember.id || "",
                    name: rawMember.name || "",
                    phone: rawMember.phone || "",
                    client_id: rawMember.client_id || "",
                    email: rawMember.email || "",
                    subscription: rawMember.subscription || "",
                    deposit: Number(rawMember.deposit_amount || 0),
                    damageCharges: Number(rawMember.damage_charges || 0),
                    remaining_amount: Number(rawMember.remaining_amount || 0),
                    credits: Number(rawMember.credits || 0),
                    billing_address: rawMember.billing_address || "",
                    overdue_charges: rawMember.overdue_charges || 0,
                    overdue_charges_details: rawMember.overdue_charges_details || []
                };
                setMemberInfo(mappedMember);
            }
            setLoadingDetails(false);
            setCartLoading(false);
            setMemberLoading(false);
            console.timeEnd("Total click-to-render duration");
            return;
        }

        setSelectedOrder(order);

        try {
            const detailsRes = await getOrderDetails({ token, orderId });
            console.log("[DEBUG] selectOrder - Order details API response:", detailsRes);

            const response = detailsRes;
            console.log(
                "Order Details Items",
                response?.data?.items
            );

            let orderData = response;
            if (response) {
                if (response.data) {
                    if (response.data.data) {
                        orderData = response.data.data;
                    } else {
                        orderData = response.data;
                    }
                }
            }

            if (orderData) {
                // Map the billing address and member info from the order details response
                if (orderData.member_info) {
                    const rawMember = orderData.member_info;
                    const mappedMember = {
                        id: rawMember.client_id || rawMember.id || "",
                        name: rawMember.name || "",
                        phone: rawMember.phone || "",
                        client_id: rawMember.client_id || "",
                        email: rawMember.email || "",
                        subscription: rawMember.subscription || "",
                        deposit: Number(rawMember.deposit_amount || 0),
                        damageCharges: Number(rawMember.damage_charges || 0),
                        remaining_amount: Number(rawMember.remaining_amount || 0),
                        credits: Number(rawMember.credits || 0),
                        billing_address: rawMember.billing_address || "",
                        overdue_charges: rawMember.overdue_charges || 0,
                        overdue_charges_details: rawMember.overdue_charges_details || []
                    };
                    setMemberInfo(mappedMember);
                    memberCacheRef.current[mappedMember.id || orderId] = mappedMember;
                }

                const finalOrderObj = normalizeOrderDetails(orderData);
                if (finalOrderObj) {
                    // Cache member_info at root level for the cached order object
                    finalOrderObj.member_info = orderData.member_info;
                    setSelectedOrder(finalOrderObj);
                    setOriginalItems(finalOrderObj.items || []);
                    orderDetailsCacheRef.current[orderId] = finalOrderObj;
                } else {
                    throw new Error("Invalid order data format received.");
                }
            } else {
                throw new Error("Order details response was empty.");
            }
        } catch (err) {
            console.error("Order Details fetch error:", err);
            setDetailsError(err.message || "Failed to load order details");
        } finally {
            setLoadingDetails(false);
            setCartLoading(false);
            setMemberLoading(false);
            console.timeEnd("Total click-to-render duration");
        }
    }, [token]);

    /* ---------- SEARCH AND SELECT ORDER BY ID ---------- */
    const searchAndSelectOrder = useCallback(async (orderId) => {
        if (!orderId) return;
        console.log("[DEBUG] searchAndSelectOrder started for orderId:", orderId);
        setDetailsError("");
        setCartError("");
        setLoadingDetails(true);
        setCartLoading(true);
        setMemberLoading(true);
        setSelectedOrder({ order_id: orderId, items: [] });

        try {
            const detailsRes = await getOrderDetails({ token, orderId });
            console.log("[DEBUG] Search Order Details API response:", detailsRes);

            const response = detailsRes;
            console.log(
                "Order Details Items",
                response?.data?.items
            );

            let orderData = response;
            if (response) {
                if (response.data) {
                    if (response.data.data) {
                        orderData = response.data.data;
                    } else {
                        orderData = response.data;
                    }
                }
            }

            console.log("get_order_details response", orderData);

            if (orderData) {
                // Map Member Info
                const rawMember = orderData.member_info || {};
                const mappedMember = {
                    id: rawMember.client_id || rawMember.id || "",
                    name: rawMember.name || "",
                    phone: rawMember.phone || "",
                    client_id: rawMember.client_id || "",
                    email: rawMember.email || "",
                    subscription: rawMember.subscription || "",
                    deposit: Number(rawMember.deposit_amount || 0),
                    damageCharges: Number(rawMember.damage_charges || 0),
                    remaining_amount: Number(rawMember.remaining_amount || 0),
                    credits: Number(rawMember.credits || 0),
                    billing_address: rawMember.billing_address || "",
                    overdue_charges: rawMember.overdue_charges || 0,
                    overdue_charges_details: rawMember.overdue_charges_details || []
                };
                setMemberInfo(mappedMember);
                memberCacheRef.current[mappedMember.id || orderId] = mappedMember;

                // Map Selected Order using normalizeOrderDetails helper
                const finalOrderObj = normalizeOrderDetails(orderData);
                if (finalOrderObj) {
                    // Cache member_info at root level for the cached order object
                    finalOrderObj.member_info = orderData.member_info;
                    console.log("mapped selectedOrder", finalOrderObj);
                    setSelectedOrder(finalOrderObj);
                    setOriginalItems(finalOrderObj.items || []);
                    orderDetailsCacheRef.current[orderId] = finalOrderObj;
                    setDetailsError("");
                    setCartError("");
                } else {
                    throw new Error("Invalid order data format received.");
                }
            } else {
                throw new Error("Order Details not found");
            }
        } catch (err) {
            console.error("Searched order fetch error:", err);
            setDetailsError(err.message || "Order not found");
            setSelectedOrder({ order_id: orderId, items: [], error: true });
            setOriginalItems([]);
        } finally {
            setLoadingDetails(false);
            setCartLoading(false);
            setMemberLoading(false);
        }
    }, [token]);

    const handleRemoveFromDelivery = (item) => {
        setPackedItems(prev => prev.filter(x => x.product_id !== item.product_id));
        showToast(`"${item.name}" removed from packed list`, "success");
    };

    const triggerCancelConfirm = () => {
        setShowCancelModal(true);
    };

    const confirmCancelOrder = async () => {
        if (!selectedOrder) return;
        const orderId = selectedOrder.order_id;
        if (!orderId) return;

        setCancelingOrder(true);
        try {
            const res = await cancelOrder({ token, orderId });
            if (res && res.success) {
                showToast(res.message || "Order cancelled successfully.", "success");
                setShowCancelModal(false);

                setSelectedOrder(prev => {
                    if (!prev) return null;
                    return {
                        ...prev,
                        order_status: "cancelled",
                        status: "cancelled"
                    };
                });

                setOrders(prev => prev.map(o => {
                    if (o.order_id === orderId) {
                        return {
                            ...o,
                            status: "cancelled"
                        };
                    }
                    return o;
                }));

                fetchStatusCounts();

                try {
                    const detailsRes = await getOrderDetails({ token, orderId });
                    const response = detailsRes;
                    console.log(
                        "Order Details Items",
                        response?.data?.items
                    );
                    let orderData = response;
                    if (response) {
                        if (response.data) {
                            if (response.data.data) {
                                orderData = response.data.data;
                            } else {
                                orderData = response.data;
                            }
                        }
                    }
                    if (orderData) {
                        const orderDataObj = normalizeOrderDetails(orderData);
                        setSelectedOrder(prev => {
                            if (!prev) return null;
                            return {
                                ...prev,
                                ...orderDataObj,
                                order_status: orderDataObj.order_status || "cancelled"
                            };
                        });
                    }
                } catch (detailsErr) {
                    console.error("Failed to refresh order details after cancellation:", detailsErr);
                }

                fetchOrdersList(1, false);
            } else {
                showToast(res?.message || "Failed to cancel order.", "error");
            }
        } catch (err) {
            showToast(err.message || "Failed to cancel order.", "error");
        } finally {
            setCancelingOrder(false);
        }
    };

    // Auto-calculate is handled directly by useDamageForm hook.

    /* ---------- HANDLE QUERY CHANGE FOR SEARCH AND MODE SWITCHING ---------- */
    useEffect(() => {
        if (debouncedOrderQuery) {
            if (String(selectedOrder?.order_id) !== String(debouncedOrderQuery)) {
                setMode("SEARCH_RESULT");
                searchAndSelectOrder(debouncedOrderQuery);
            }
        } else {
            if (mode !== "ALL_ORDERS") {
                setMode("INITIAL");
                setSelectedOrder(null);
                setMemberInfo(null);
                setDetailsError("");
                setCartError("");
            }
        }
    }, [debouncedOrderQuery, mode, selectedOrder?.order_id, searchAndSelectOrder]);

    const handleCardAction = async (item, action) => {
        if (action === "damaged") {
            setDamageTargetItem(item);
            setShowDamageModal(true);
            return;
        }

        if (action === "restock") {
            if (!window.confirm(`Are you sure you want to restock "${item.name}"?`)) return;
            setActionSubmitting(true);
            try {
                const payload = {
                    product_id: Number(item.product_id),
                    copy_post_id: item.copy_post_id,
                    hold_status: "restock",
                    reason: "Restocked during card-level fulfillment workflow",
                    remark: "Restocked copy",
                    email_notification: false,
                    images: []
                };

                await updateBookHoldStatus({ token, payload });
                showToast(`"${item.name}" successfully updated to Restocked!`, "success");

                setSelectedOrder(prev => filterCartItem(prev, item.product_id));
                setPackedItems(prev => prev.filter(x => x.product_id !== item.product_id));
            } catch (e) {
                showToast(e.message || "Failed to update restock status", "error");
            } finally {
                setActionSubmitting(false);
            }
            return;
        }

        if (action === "missing") {
            if (!window.confirm(`Are you sure you want to mark "${item.name}" as Missing?`)) return;
            setActionSubmitting(true);
            try {
                const payload = {
                    product_id: Number(item.product_id),
                    copy_post_id: item.copy_post_id,
                    hold_status: "missing",
                    reason: "Marked missing during card-level fulfillment workflow",
                    remark: "Missing copy",
                    email_notification: false,
                    images: []
                };

                await updateBookHoldStatus({ token, payload });
                showToast(`"${item.name}" successfully updated to Missing!`, "success");

                setSelectedOrder(prev => filterCartItem(prev, item.product_id));
                setPackedItems(prev => prev.filter(x => x.product_id !== item.product_id));
            } catch (e) {
                showToast(e.message || "Failed to update missing status", "error");
            } finally {
                setActionSubmitting(false);
            }
            return;
        }

        if (action === "lost") {
            setDamageRemoveFromOrder(true);
            setDamageTargetItem({ ...item, initialReason: "Book Lost" });
            setShowDamageModal(true);
            return;
        }

        if (action === "issued") {
            const isDuplicate = packedItems.some(p => String(p.product_id) === String(item.product_id));
            if (isDuplicate) {
                showToast("This book is already selected.", "error");
                return;
            }
            setPackedItems(prev => [...prev, item]);
            showToast(`"${item.name}" marked as Issued`, "success");
            return;
        }
    };

    const handleStartScanReplace = (item) => {
        setReplacingItem(item);
        setScannedReplaceId("");
        setScanReplaceError("");
        setShowScanReplaceModal(true);
    };

    const handleScanReplaceSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!scannedReplaceId.trim() || !replacingItem) return;

        setScanningReplace(true);
        setScanReplaceError("");
        try {
            const fetchedProduct = await getProductById({ id: scannedReplaceId.trim(), token });
            if (!fetchedProduct) {
                throw new Error("Product not found. Please verify the ID.");
            }

            const newItem = {
                name: fetchedProduct.name || fetchedProduct.title || "Scanned Replacement",
                product_id: fetchedProduct.id,
                quantity: 1,
                price: Number(fetchedProduct.price || fetchedProduct.credits || 0),
                credits: Number(fetchedProduct.price || fetchedProduct.credits || 0),
                issue_or_return: "issue",
                status: "Issuing",
                image: fetchedProduct.image || "",
                product_type: fetchedProduct.product_type || "book",
                line_subtotal: Number(fetchedProduct.price || fetchedProduct.credits || 0),
                stock_status: fetchedProduct.stock_status || "instock",
                copy_post_id: fetchedProduct.copy_post_id || 0
            };

            setSelectedOrder(prev => {
                if (!prev) return prev;
                const filtered = prev.items.filter(x => x.product_id !== replacingItem.product_id);
                const oldItem = prev.items.find(x => x.product_id === replacingItem.product_id);
                const oldPrice = Number(oldItem?.price || 0) * Number(oldItem?.quantity || 1);
                const newPrice = Number(newItem.price) * Number(newItem.quantity);
                const priceDiff = newPrice - oldPrice;
                return {
                    ...prev,
                    items: [...filtered, newItem],
                    cart_subtotal: Math.max(0, prev.cart_subtotal + priceDiff),
                    cart_total: Math.max(0, prev.cart_total + priceDiff)
                };
            });

            setPackedItems(prev => {
                const filtered = prev.filter(x => x.product_id !== replacingItem.product_id);
                return [...filtered, newItem];
            });

            showToast(`Successfully replaced "${replacingItem.name}" with "${newItem.name}"`, "success");
            setShowScanReplaceModal(false);
            setReplacingItem(null);
        } catch (err) {
            setScanReplaceError(err.message || "Failed to find product by ID");
        } finally {
            setScanningReplace(false);
        }
    };

    const handleManualAddBook = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // If we have a selected book from the search dropdown
        if (selectedBookFromSearch) {
            const isDuplicate = packedItems.some(item =>
                String(item.product_id) === String(selectedBookFromSearch.id)
            );

            if (isDuplicate) {
                showToast("This copy/book is already packed.", "error");
                return;
            }

            const newItem = {
                product_id: selectedBookFromSearch.id,
                name: selectedBookFromSearch.name || selectedBookFromSearch.title || "Selected Book",
                image: selectedBookFromSearch.image || selectedBookFromSearch.img || "",
                product_type: selectedBookFromSearch.product_type || "book",
                price: Number(selectedBookFromSearch.price || selectedBookFromSearch.credits || 0),
                credits: Number(selectedBookFromSearch.credits || selectedBookFromSearch.price || 0),
                issue_or_return: "issue",
                quantity: 1,
                line_subtotal: Number(selectedBookFromSearch.price || selectedBookFromSearch.credits || 0),
                stock_status: selectedBookFromSearch.stock_status || "instock",
                copy_post_id: selectedBookFromSearch.copy_post_id || 0
            };

            setPackedItems(prev => [...prev, newItem]);
            showToast(`"${newItem.name}" added to packing list`, "success");
            setCopySearchQuery("");
            setSelectedBookFromSearch(null);
            setShowDropdown(false);
            return;
        }

        const query = copySearchQuery.trim();
        if (!query) return;

        // Try to match with books in the selected order (left side list)
        const matchingItem = selectedOrder?.items?.find(item => {
            return String(item.copy_post_id) === query ||
                   String(item.product_id) === query ||
                   String(item.isbn) === query ||
                   String(item.sku) === query;
        });

        if (matchingItem) {
            // Check if only Issue status is actionable on packing screen
            const orderStatus = (selectedOrder?.order_status || selectedOrder?.status || "").toLowerCase();
            const screenType = (orderStatus === "completed" || orderStatus === "wc-completed") ? "delivery" : "packing";
            const isIssue = (matchingItem.issue_or_return || "issue") === "issue";

            if (screenType === "packing" && !isIssue) {
                showToast("Only issue books are actionable on the packing screen.", "error");
                return;
            }

            const isDuplicate = packedItems.some(item =>
                String(item.product_id) === String(matchingItem.product_id)
            );

            if (isDuplicate) {
                showToast("This book is already packed.", "error");
                return;
            }

            setPackedItems(prev => [...prev, matchingItem]);
            showToast(`"${matchingItem.name}" packed successfully!`, "success");
            setCopySearchQuery("");
            return;
        }

        // If not found in the order, search database (manual add)
        setCopySearchLoading(true);
        try {
            // First try searching by copy_id
            let res = await searchProducts({
                token,
                copy_id: query,
                page: 1,
                perPage: 1,
            });

            // If not found by copy_id, try smart_product_id (numeric) or generic query
            if (!res?.data || res.data.length === 0) {
                if (/^\d+$/.test(query)) {
                    res = await searchProducts({
                        token,
                        smart_product_id: query,
                        page: 1,
                        perPage: 1,
                    });
                } else {
                    res = await searchProducts({
                        token,
                        q: query,
                        page: 1,
                        perPage: 1,
                    });
                }
            }

            if (res?.data && res.data.length > 0) {
                const fetchedProduct = res.data[0];

                const isDuplicate = packedItems.some(item =>
                    String(item.product_id) === String(fetchedProduct.id)
                );

                if (isDuplicate) {
                    showToast("This book is already packed.", "error");
                    return;
                }

                const newItem = {
                    product_id: fetchedProduct.id,
                    name: fetchedProduct.name || fetchedProduct.title || "Scanned Book",
                    image: fetchedProduct.image || "",
                    product_type: fetchedProduct.product_type || "book",
                    price: Number(fetchedProduct.price || 0),
                    credits: Number(fetchedProduct.credits || fetchedProduct.price || 0),
                    issue_or_return: "issue",
                    quantity: 1,
                    line_subtotal: Number(fetchedProduct.price || 0),
                    stock_status: fetchedProduct.stock_status || "instock",
                    copy_post_id: Number(query) || fetchedProduct.copy_post_id || 0
                };

                setPackedItems(prev => [...prev, newItem]);
                showToast(`"${newItem.name}" added to packing list`, "success");
                setCopySearchQuery("");
                setShowDropdown(false);
            } else {
                showToast("No book found matching this Copy ID.", "error");
            }
        } catch (err) {
            console.error("Manual add book search failed", err);
            showToast("Error searching Copy ID. Please try again.", "error");
        } finally {
            setCopySearchLoading(false);
        }
    };

    const handleDamageSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        await damageFormObj.handleSaveDamage();
    };

    const handleSubmitAction = useCallback(async () => {
        if (!selectedOrder || actionSubmitting) return;

        const orderId = selectedOrder.order_id;
        if (!orderId) {
            showToast("No active order selected", "error");
            return;
        }

        setActionSubmitting(true);

        try {
            // Calculate added and removed items dynamically
            const add_items = [];
            const remove_items = [];

            // We submit only the items present in the packedItems list
            packedItems.forEach(item => {
                const original = originalItems.find(o => String(o.product_id) === String(item.product_id));
                if (!original) {
                    add_items.push({
                        product_id: Number(item.product_id),
                        quantity: Number(item.quantity || 1)
                    });
                }
            });

            originalItems.forEach(item => {
                const current = packedItems.find(c => String(c.product_id) === String(item.product_id));
                if (!current) {
                    remove_items.push(Number(item.product_id));
                }
            });

            const status = selectedOrder.order_status || selectedOrder.status || "";

            const payload = {
                order_id: Number(orderId),
                status: status,
                add_items: add_items,
                remove_items: remove_items
            };

            const res = await updateOrder({ token, payload });

            if (res && res.success === false) {
                throw new Error(res.message || "Failed to update order");
            }

            showToast("Order updated successfully!", "success");

            // On success, refresh order details
            // We can delete cache to ensure we fetch fresh details
            delete orderDetailsCacheRef.current[orderId];

            // Re-fetch order details
            const detailsRes = await getOrderDetails({ token, orderId });
            let orderData = detailsRes;
            if (detailsRes?.data) {
                orderData = detailsRes.data.data || detailsRes.data;
            }

            if (orderData) {
                const finalOrderObj = normalizeOrderDetails(orderData);
                if (finalOrderObj) {
                    finalOrderObj.member_info = orderData.member_info;
                    setSelectedOrder(finalOrderObj);
                    setOriginalItems(finalOrderObj.items || []);
                    orderDetailsCacheRef.current[orderId] = finalOrderObj;

                    // Update member info as well
                    if (orderData.member_info) {
                        const rawMember = orderData.member_info;
                        const mappedMember = {
                            id: rawMember.client_id || rawMember.id || "",
                            name: rawMember.name || "",
                            phone: rawMember.phone || "",
                            client_id: rawMember.client_id || "",
                            email: rawMember.email || "",
                            subscription: rawMember.subscription || "",
                            deposit: Number(rawMember.deposit_amount || 0),
                            damageCharges: Number(rawMember.damage_charges || 0),
                            remaining_amount: Number(rawMember.remaining_amount || 0),
                            credits: Number(rawMember.credits || 0),
                            billing_address: rawMember.billing_address || "",
                            overdue_charges: rawMember.overdue_charges || 0,
                            overdue_charges_details: rawMember.overdue_charges_details || []
                        };
                        setMemberInfo(mappedMember);
                    }
                }
            }

            // Clear temporary edit variables
            setReplacingItem(null);
            setScannedReplaceId("");
            setScanReplaceError("");

            // Clear packed items after successful submit
            setPackedItems([]);

            // Fetch order status counts and order list to update left panel
            fetchStatusCounts();
            fetchOrdersList(1, false);

        } catch (err) {
            showToast(err.message || "Failed to update order", "error");
        } finally {
            setActionSubmitting(false);
        }
    }, [
        selectedOrder,
        actionSubmitting,
        originalItems,
        packedItems,
        token,
        showToast,
        fetchStatusCounts,
        fetchOrdersList,
        setReplacingItem,
        setScannedReplaceId,
        setScanReplaceError
    ]);

    const handleClearActions = useCallback(async () => {
        if (!selectedOrder || !selectedOrder.order_id) return;
        const orderId = selectedOrder.order_id;

        // Remove from cache to force a fresh fetch from API
        delete orderDetailsCacheRef.current[orderId];

        // Fetch original unmodified data
        await selectOrder({ order_id: orderId });
        setPackedItems([]);
        showToast("Pending actions cleared", "success");
    }, [selectedOrder, selectOrder, showToast]);

    const handleStatusChange = useCallback((newStatus) => {
        if (!selectedOrder) return;
        const orderId = selectedOrder.order_id;
        if (!orderId) return;

        setSelectedOrder((prev) => {
            if (!prev) return null;
            return {
                ...prev,
                order_status: newStatus,
                status: newStatus,
            };
        });

        if (orderDetailsCacheRef.current[orderId]) {
            orderDetailsCacheRef.current[orderId] = {
                ...orderDetailsCacheRef.current[orderId],
                order_status: newStatus,
                status: newStatus,
            };
        }

        setOrders((prev) =>
            prev.map((o) => {
                if (o.order_id === orderId) {
                    return {
                        ...o,
                        status: newStatus,
                    };
                }
                return o;
            })
        );

        showToast(`Order status updated to ${newStatus.toUpperCase()}`, "success");
    }, [selectedOrder, showToast]);

    const handleAllOrdersClick = useCallback(() => {
        setMode("ALL_ORDERS");
        setSelectedOrder(null);
        setOrderQuery("");
        setDebouncedOrderQuery("");
        setAllOrdersQuery("");
        setDebouncedAllOrdersQuery("");
        setActiveStatusFilter("all");
    }, []);

    const handleBackClick = useCallback(() => {
        setMode("INITIAL");
        setSelectedOrder(null);
        setOrders([]);
        setOrderQuery("");
        setDebouncedOrderQuery("");
        setAllOrdersQuery("");
        setDebouncedAllOrdersQuery("");
    }, []);

    return {
        mode,
        setMode,
        handleAllOrdersClick,
        handleBackClick,
        searchAndSelectOrder,
        token,
        orders,
        orderQuery,
        setOrderQuery,
        debouncedOrderQuery,
        setDebouncedOrderQuery,
        allOrdersQuery,
        setAllOrdersQuery,
        debouncedAllOrdersQuery,
        setDebouncedAllOrdersQuery,
        loadingOrders,
        loadingMoreOrders,
        statusCounts,
        countsLoaded,
        listContainerRef,
        loadingDetails,
        detailsError,
        cartLoading,
        cartError,
        cancelingOrder,
        memberLoading,
        memberInfo,
        selectedOrder,
        setSelectedOrder,
        toast,
        showToast,
        showDamageModal,
        setShowDamageModal,
        showCancelModal,
        setShowCancelModal,
        damageSubmitting,
        damageRemoveFromOrder,
        setDamageRemoveFromOrder,
        damageForm,
        setDamageForm,
        actionSubmitting,
        damageTargetItem,
        setDamageTargetItem,
        replacingItem,
        setReplacingItem,
        showScanReplaceModal,
        setShowScanReplaceModal,
        scannedReplaceId,
        setScannedReplaceId,
        scanningReplace,
        scanReplaceError,
        activeLeftDropdown,
        setActiveLeftDropdown,
        activeStatusFilter,
        setActiveStatusFilter,
        selectOrder,
        handleRemoveFromDelivery,
        triggerCancelConfirm,
        confirmCancelOrder,
        handleCardAction,
        handleStartScanReplace,
        handleScanReplaceSubmit,
        handleDamageSubmit,
        handleSubmitAction,
        packedItems,
        setPackedItems,
        fetchStatusCounts,
        fetchOrdersList,
        loadMoreOrders,
        handleClearActions,
        handleStatusChange,
        orderStatuses,
        statusesLoading,
        statusesError,
        copySearchQuery,
        setCopySearchQuery,
        copySearchLoading,
        handleManualAddBook,
        searchResults,
        searchLoading,
        showDropdown,
        setShowDropdown,
        selectedBookFromSearch,
        setSelectedBookFromSearch,
        copyOptions: damageFormObj.copyOptions,
        copiesLoading: damageFormObj.copiesLoading
    };
}
